import { z } from 'zod';
import { formatCustomerMessage, getReasonCode, ReasonCodeSchema, } from '../reason-codes.js';
/**
 * Errores RFC 9457 (`application/problem+json`):
 * `{ type, title, status, reasonCode, message, detail, traceId, errors? }`.
 * `message` es apto para el cliente final (es-CO); `detail` es técnico (operador).
 */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';
export const PROBLEM_TYPE_BASE = 'https://docs.paguz.co/problems/';
export const ProblemFieldErrorSchema = z
    .object({
    path: z
        .string()
        .meta({ description: 'Ruta del campo (JSON pointer simplificado, p. ej. `amount.amount`).' }),
    message: z.string(),
    code: z.string().optional(),
})
    .meta({ id: 'ProblemFieldError' });
export const ProblemSchema = z
    .object({
    type: z.string(),
    title: z.string(),
    status: z.int().min(100).max(599),
    reasonCode: ReasonCodeSchema,
    message: z.string().meta({ description: 'Mensaje para el cliente final (es-CO).' }),
    detail: z.string().optional().meta({ description: 'Detalle técnico (operador).' }),
    instance: z.string().optional(),
    traceId: z.string().optional(),
    errors: z.array(ProblemFieldErrorSchema).optional(),
})
    .meta({ id: 'Problem', description: 'Error RFC 9457 (application/problem+json).' });
/**
 * Error de dominio con código Paguz. El dominio lanza `ProblemError`; `server-kit` lo serializa
 * como `Problem` con el `traceId` de la petición.
 */
export class ProblemError extends Error {
    reasonCode;
    status;
    detail;
    params;
    errors;
    constructor(reasonCode, options = {}) {
        const definition = getReasonCode(reasonCode);
        super(`${reasonCode}: ${options.detail ?? definition.operatorDetail}`, {
            cause: options.cause,
        });
        this.name = 'ProblemError';
        this.reasonCode = reasonCode;
        this.status = options.status ?? definition.httpStatus;
        this.detail = options.detail;
        this.params = options.params ?? {};
        this.errors = options.errors;
    }
    get retryable() {
        return getReasonCode(this.reasonCode).retryable;
    }
    toProblem(context = {}) {
        return toProblem(this.reasonCode, {
            ...(this.detail !== undefined && { detail: this.detail }),
            params: this.params,
            ...(this.errors !== undefined && { errors: this.errors }),
            status: this.status,
            ...context,
        });
    }
}
export function isProblemError(error) {
    return error instanceof ProblemError;
}
/** Construye un `Problem` a partir de un código del catálogo. */
export function toProblem(reasonCode, options = {}) {
    const definition = getReasonCode(reasonCode);
    return {
        type: `${PROBLEM_TYPE_BASE}${reasonCode}`,
        title: definition.operatorDetail,
        status: options.status ?? definition.httpStatus,
        reasonCode,
        message: formatCustomerMessage(reasonCode, options.params),
        detail: options.detail ?? definition.operatorDetail,
        ...(options.instance !== undefined && { instance: options.instance }),
        ...(options.traceId !== undefined && { traceId: options.traceId }),
        ...(options.errors !== undefined && { errors: options.errors }),
    };
}
//# sourceMappingURL=problem.js.map